"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect } from "react";
import { betslipApi } from "fansunited-frontend-components";
import type { BetslipState } from "@/features/fu-widgets/library-types";
import { selectionId } from "@/features/fu-widgets/library-types";
const STORAGE_KEY = "fu_betslip";
const STORAGE_VERSION = 2;
const EXPIRY_MS = 3 * 60 * 60 * 1000;
const SAVE_DEBOUNCE_MS = 150;
interface PersistedPayload {
  version: 2;
  selections: string[];
  kickoffAtById: Record<string, string>;
}

/**
 * Side map updated by addPrediction / addOddsSelection. Lives in-module
 * (not in React state) because the library bus holds selection IDs only
 * and we need to persist kickoff times to filter expired items on reload.
 */
const kickoffAtById: Record<string, string> = {};
export function recordKickoff(id: string, kickoffAt?: string): void {
  if (kickoffAt) kickoffAtById[id] = kickoffAt;
}
function readStorage(): PersistedPayload | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object" || (parsed as {
      version?: unknown;
    }).version !== STORAGE_VERSION || !Array.isArray((parsed as {
      selections?: unknown;
    }).selections)) {
      // Includes legacy v1 payloads — silently ignore and move on.
      return null;
    }
    return parsed as PersistedPayload;
  } catch {
    return null;
  }
}
function writeStorage(payload: PersistedPayload): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // Quota exceeded or disabled — silently drop.
  }
}

/**
 * Called once on CanvasBetslip mount. Reads persisted selections, filters
 * out items whose kickoff is more than 3 hours in the past, and replays
 * each survivor into the library bus. The library queues pre-mount calls.
 */
export function hydrateFromStorage(): void {
  const payload = readStorage();
  if (!payload) return;
  const now = Date.now();
  const survivors: string[] = [];
  for (const id of payload.selections) {
    const kickoff = payload.kickoffAtById[id];
    if (kickoff) {
      const kickoffMs = Date.parse(kickoff);
      if (!isNaN(kickoffMs) && now - kickoffMs >= EXPIRY_MS) continue;
      kickoffAtById[id] = kickoff;
    }
    survivors.push(id);
  }
  for (const id of survivors) {
    betslipApi.setSelection(id);
  }
}

/**
 * Subscribe to the bus and save to localStorage on every change, debounced.
 * Call from a `useEffect` in <CanvasBetslip />.
 */
export function useBetslipPersistence() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  useEffect(_temp2, t0);
}
function _temp2() {
  let timer = null;
  const unsubscribe = betslipApi.subscribe(state => {
    if (timer) {
      clearTimeout(timer);
    }
    timer = setTimeout(() => {
      const ids = state.selections.map(_temp);
      const prunedKickoffs = {};
      for (const id of ids) {
        if (kickoffAtById[id]) {
          prunedKickoffs[id] = kickoffAtById[id];
        }
      }
      writeStorage({
        version: STORAGE_VERSION,
        selections: ids,
        kickoffAtById: prunedKickoffs
      });
    }, SAVE_DEBOUNCE_MS);
  });
  return () => {
    if (timer) {
      clearTimeout(timer);
    }
    unsubscribe();
  };
}
function _temp(s) {
  return selectionId(s);
}